import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Zap,
  ArrowRight,
  Clock,
  BarChart3,
  Copy,
  Check,
  ExternalLink,
  Shield,
  Users,
  Star,
  Play,
  ChevronRight,
  Boxes,
  Globe,
} from 'lucide-react';
import { toast } from 'sonner';

// ── Mock data — en producción viene del backend via GET /api/workflows/public/:id ──

interface PublicNode {
  id: string;
  name: string;
  category: string;
  color: string;
  description: string;
}

interface PublicWorkflowData {
  id: string;
  name: string;
  description: string;
  nodeCount: number;
  executions: number;
  timeSpent: number;
  createdBy: string;
  authorBio?: string;
  category: string;
  tags: string[];
  nodes: PublicNode[];
  usedBy: number;       // cuántos han clonado
  rating: number;       // 1-5
  createdAt: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  triggers:     'bg-blue-500',
  actions:      'bg-green-500',
  logic:        'bg-yellow-500',
  data:         'bg-orange-500',
  integrations: 'bg-purple-500',
  ai:           'bg-pink-500',
  blockchain:   'bg-indigo-500',
  social:       'bg-cyan-500',
};

function getMockWorkflow(id: string): PublicWorkflowData {
  // En producción: fetch('/api/workflows/public/' + id)
  return {
    id,
    name: 'Envío Automático de Ofertas',
    description: 'Este workflow se dispara cada lunes a las 9:00, obtiene la lista de clientes activos de la base de datos, genera una oferta personalizada con IA para cada uno, y la envía por email. Sin intervención humana.',
    nodeCount: 8,
    executions: 24,
    timeSpent: 10,
    createdBy: 'Juan Pérez',
    authorBio: 'Automatizador serial. Equipo de marketing en empresa B2B.',
    category: 'Marketing',
    tags: ['email', 'ia', 'marketing', 'ofertas'],
    usedBy: 47,
    rating: 4.8,
    createdAt: '2024-01-15',
    nodes: [
      { id: 'n1', name: 'Schedule Trigger', category: 'triggers', color: 'bg-blue-500', description: 'Lunes 09:00 · Zona horaria Madrid' },
      { id: 'n2', name: 'Query SQL', category: 'data', color: 'bg-orange-500', description: 'Lista clientes activos del último mes' },
      { id: 'n3', name: 'Loop', category: 'logic', color: 'bg-yellow-500', description: 'Itera sobre cada cliente' },
      { id: 'n4', name: 'Claude AI', category: 'ai', color: 'bg-pink-500', description: 'Genera oferta personalizada según historial' },
      { id: 'n5', name: 'If/Else', category: 'logic', color: 'bg-yellow-500', description: 'Si cliente VIP → plantilla premium' },
      { id: 'n6', name: 'Send Email SMTP', category: 'actions', color: 'bg-green-500', description: 'Envía email con oferta generada' },
      { id: 'n7', name: 'SQL Insert', category: 'data', color: 'bg-orange-500', description: 'Registra envío en base de datos' },
      { id: 'n8', name: 'Slack Notification', category: 'integrations', color: 'bg-purple-500', description: 'Notifica al equipo: resumen del lote' },
    ],
  };
}

// ── Node flow visual ──────────────────────────────────────────────────────────

function NodeFlowPreview({ nodes }: { nodes: PublicNode[] }) {
  return (
    <div className="overflow-x-auto pb-2">
      <div className="flex items-center gap-1 min-w-max">
        {nodes.map((node, idx) => (
          <React.Fragment key={node.id}>
            <div className="flex flex-col items-center gap-1.5 w-24">
              <div className={`h-10 w-10 rounded-xl ${node.color} flex items-center justify-center shadow-sm`}>
                <Boxes className="h-5 w-5 text-white" />
              </div>
              <p className="text-[10px] font-medium text-center leading-tight line-clamp-2">{node.name}</p>
              <Badge variant="secondary" className="text-[9px] px-1.5 py-0 h-4">{node.category}</Badge>
            </div>
            {idx < nodes.length - 1 && (
              <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mx-1" />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function PublicWorkflowPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const workflow = getMockWorkflow(id || 'demo');
  const timeSaved = Math.round(workflow.executions * workflow.nodeCount * 0.1 * 10) / 10;
  const currentUrl = window.location.href;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(currentUrl);
    setCopied(true);
    toast.success('Link copiado');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleUseTemplate = () => {
    // Intenta navegar al dashboard. Si no está logado, irá al login.
    navigate('/login?redirect=/dashboard/workflows/new&template=' + workflow.id);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* ── Top Bar ── */}
      <header className="border-b border-border bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo-zaphub.png" alt="ZAPHUB" className="h-7 w-7 object-contain" />
            <span className="font-bold text-base">ZAPHUB</span>
            <Badge variant="secondary" className="text-xs hidden sm:flex">
              <Globe className="h-3 w-3 mr-1" />
              Workflow público
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleCopyLink}>
              {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              <span className="hidden sm:inline ml-1.5">Copiar link</span>
            </Button>
            <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={() => navigate('/login')}>
              Entrar en ZAPHUB
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10 space-y-8">

        {/* ── Hero ── */}
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 flex items-center justify-center flex-shrink-0">
              <img src="/logo-zaphub.png" alt="ZAPHUB" className="h-14 w-14 object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Badge className="bg-primary/10 text-primary border-primary/20 text-xs">{workflow.category}</Badge>
                {workflow.tags.map(tag => (
                  <Badge key={tag} variant="secondary" className="text-xs">#{tag}</Badge>
                ))}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold leading-tight">{workflow.name}</h1>
              <p className="text-muted-foreground mt-2 text-sm sm:text-base leading-relaxed">{workflow.description}</p>
            </div>
          </div>

          {/* Stats bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl border border-border bg-card p-3 text-center">
              <p className="text-2xl font-bold text-primary">{workflow.nodeCount}</p>
              <p className="text-xs text-muted-foreground mt-0.5">nodos</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-3 text-center">
              <p className="text-2xl font-bold text-green-500">{workflow.executions}</p>
              <p className="text-xs text-muted-foreground mt-0.5">ejecuciones</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-3 text-center">
              <p className="text-2xl font-bold text-purple-500">{timeSaved > 0 ? `${timeSaved}h` : `${workflow.timeSpent}h`}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{timeSaved > 0 ? 'ahorradas' : 'para crear'}</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-3 text-center">
              <p className="text-2xl font-bold text-orange-500">{workflow.usedBy}</p>
              <p className="text-xs text-muted-foreground mt-0.5">lo usan</p>
            </div>
          </div>
        </div>

        {/* ── CTA Principal ── */}
        <Card className="relative overflow-hidden border-primary/30 bg-gradient-to-br from-primary/5 via-background to-purple-500/5">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-purple-500 to-blue-500" />
          <CardContent className="py-6">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1 text-center sm:text-left">
                <h2 className="text-lg font-bold mb-1">Usa esta automatización gratis</h2>
                <p className="text-sm text-muted-foreground">
                  Clona este workflow en ZAPHUB en un clic. Sin programar. Empieza a ahorrar{' '}
                  {timeSaved > 0 ? `${timeSaved}h semanales` : 'horas de trabajo'} hoy mismo.
                </p>
              </div>
              <div className="flex flex-col gap-2 flex-shrink-0 w-full sm:w-auto">
                <Button
                  size="lg"
                  className="bg-primary hover:bg-primary/90 w-full sm:w-auto"
                  onClick={handleUseTemplate}
                >
                  <Play className="mr-2 h-4 w-4" />
                  Usar esta plantilla
                </Button>
                <p className="text-[11px] text-muted-foreground text-center">
                  Gratis · Sin tarjeta · Listo en 2 minutos
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Node Flow ── */}
        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary" />
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Boxes className="h-4 w-4 text-primary" />
              Flujo de nodos
            </CardTitle>
            <p className="text-xs text-muted-foreground">Vista de solo lectura — clona el workflow para editarlo</p>
          </CardHeader>
          <CardContent>
            <NodeFlowPreview nodes={workflow.nodes} />

            {/* Node details */}
            <div className="mt-4 space-y-2">
              {workflow.nodes.map((node, idx) => (
                <div key={node.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                  <span className="text-xs text-muted-foreground w-5 text-right flex-shrink-0">{idx + 1}</span>
                  <div className={`h-7 w-7 rounded-lg ${node.color} flex items-center justify-center flex-shrink-0`}>
                    <Boxes className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{node.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{node.description}</p>
                  </div>
                  <Badge variant="secondary" className="text-[10px] flex-shrink-0">{node.category}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* ── Author ── */}
        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary" />
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-lg">{workflow.createdBy.charAt(0)}</span>
              </div>
              <div className="flex-1">
                <p className="font-semibold">{workflow.createdBy}</p>
                {workflow.authorBio && (
                  <p className="text-sm text-muted-foreground">{workflow.authorBio}</p>
                )}
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                    {workflow.rating}/5
                  </span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {workflow.usedBy} usuarios
                  </span>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-xs text-muted-foreground">Publicado</p>
                <p className="text-xs font-medium">{new Date(workflow.createdAt).toLocaleDateString('es', { month: 'long', year: 'numeric' })}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Why ZAPHUB ── */}
        <div className="rounded-xl border border-border bg-card p-6 space-y-4">
          <h3 className="font-bold text-base">¿Por qué ZAPHUB?</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { icon: Zap, color: 'text-primary', title: 'Automatización real', desc: '40+ integraciones · IA · Blockchain · Sin límite de ejecuciones' },
              { icon: Shield, color: 'text-green-500', title: 'Tokeniza activos', desc: 'El único sistema que une automatización + tokenización RWA en Polygon' },
              { icon: BarChart3, color: 'text-purple-500', title: 'ROI medido', desc: 'Ve exactamente cuánto tiempo y dinero ahorras cada semana' },
            ].map((item) => (
              <div key={item.title} className="flex gap-3">
                <item.icon className={`h-5 w-5 ${item.color} flex-shrink-0 mt-0.5`} />
                <div>
                  <p className="text-sm font-semibold">{item.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Bottom CTA ── */}
        <div className="text-center space-y-3 py-4">
          <Button size="lg" className="bg-primary hover:bg-primary/90 px-8" onClick={handleUseTemplate}>
            Empezar gratis con esta plantilla
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <p className="text-xs text-muted-foreground">
            Ya tienen cuenta?{' '}
            <button className="text-primary hover:underline" onClick={() => navigate('/login')}>
              Inicia sesión
            </button>
          </p>
        </div>
      </main>

      {/* ── Footer ── */}
      <footer className="border-t border-border mt-8">
        <div className="max-w-4xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <img src="/logo-zaphub.png" alt="ZAPHUB" className="h-5 w-5 object-contain" />
            <span className="text-sm font-semibold">ZAPHUB</span>
            <span className="text-xs text-muted-foreground">· Automatización empresarial + Blockchain</span>
          </div>
          <a
            href="https://zaphub.ffollowme.com"
            className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
          >
            zaphub.ffollowme.com
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </footer>
    </div>
  );
}
